import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import axiosInstance from '../../../axiosInstance';
import { getCurrentUser } from '../../../services/authService';
import { extractPrivilegesFromResponse } from '../../../hooks/utils/privilegesUtils';
import { EXTERNAL_MY_ORG_PRIVILEGES } from '../../../constants/privilege.constant';
import { RESOURCES } from '../../../constants/resourceConstants';

import { usePageTitle } from '../../../contexts/PageTitleContext';
import StyledTabs from '../../common/StyledTabs/StyledTabs';
import OrganizationDetails from '../../Admin/Organization/details';
import OrgTree from '../../common/Org Tree/tree';

const Organization = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [organization, setOrganization] = useState(null);
  const [userPrivileges, setUserPrivileges] = useState({});
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await getCurrentUser();
        if (response) {
          const privileges = extractPrivilegesFromResponse(
            response?.allPrivileges
          );
          const isExternalUser = response.user && !response.user.is_internal;
          setUserPrivileges({
            privileges,
            isExternalUser,
          });
        }
      } catch (err) {
        console.error('Error fetching current user:', err);
      }
    };
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        const response = await axiosInstance.get(
          `/api/v1/organizations/${id}`,
          {
            withCredentials: true,
          }
        );
        const data = response.data.data;
        setOrganization(data);
      } catch (err) {
        const errorMsg =
          err.response?.data?.error?.message ||
          err.response?.data?.message ||
          t('error_fetching_organization') ||
          'Error fetching organization';
        toast.error(errorMsg);
      }
    };

    if (id) fetchOrganization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Set dynamic page title based on organization name
  const { setPageTitle, clearPageTitle } = usePageTitle();

  useEffect(() => {
    if (organization?.name) {
      setPageTitle(organization.name);
    }
    return () => clearPageTitle();
  }, [organization?.name, setPageTitle, clearPageTitle]);

  const orgTabConfigs = [
    { key: 'orgDetails', label: t('orgDetails') },
    { key: 'orgTree', label: t('orgStructure') },
  ];

  const handleTabChange = (_, value) => {
    setActiveTab(value);
  };

  const editOrgPrivExists = userPrivileges?.privileges?.find((priv) => {
    return (
      priv.name === EXTERNAL_MY_ORG_PRIVILEGES.EDIT_MY_ORG &&
      priv.Resource.name === RESOURCES.EXTERNAL_MY_ORG_PRIVILEGES
    );
  })
    ? true
    : false;

  return (
    <div>
      <StyledTabs
        value={activeTab}
        onChange={handleTabChange}
        tabs={orgTabConfigs}
      />
      {activeTab === 0 && (
        <OrganizationDetails
          showEditButton={editOrgPrivExists}
          currentOrganization={organization}
        />
      )}
      {activeTab === 1 && <OrgTree userPrivileges={userPrivileges} />}
    </div>
  );
};

export default Organization;
